import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../utils/config";

function displayOrDash(val) {
  const s = typeof val === "string" ? val.trim() : val;
  return s ? String(s) : "-";
}

function getEventName(row) {
  if (row?.event_name) return row.event_name;
  if (row?.borrow_batch?.event_name) return row.borrow_batch.event_name;
  if (row?.batch?.event_name) return row.batch.event_name;
  return null;
}

function getEventPicName(row) {
  if (row?.event_pic_name) return row.event_pic_name;
  if (row?.borrow_batch?.event_pic_name) return row.borrow_batch.event_pic_name;
  if (row?.batch?.event_pic_name) return row.batch.event_pic_name;
  return null;
}

function getOutstandingQty(it) {
  const direct =
    it?.outstanding_quantity ??
    it?.remaining_quantity ??
    it?.remaining_qty ??
    it?.qty_remaining ??
    it?.remaining;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    return Number.isFinite(n) ? n : 0;
  }

  const borrowed =
    Number(it?.borrowed_quantity ?? it?.borrowed_qty ?? it?.quantity ?? 0) || 0;

  const returned =
    Number(
      it?.returned_quantity ??
      it?.returned_qty ??
      it?.returned ??
      it?.total_returned ??
      ((Number(it?.returned_good ?? it?.good ?? 0) || 0) +
        (Number(it?.returned_damaged ?? it?.damaged ?? 0) || 0))
    ) || 0;

  const remaining = borrowed - returned;
  return remaining > 0 ? remaining : 0;
}

export default function ActiveBorrows() {
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);

  const token = localStorage.getItem("accessToken");
  const navigate = useNavigate();

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editForm, setEditForm] = useState({
    id: null,
    event_name: "",
    event_pic_name: "",
    borrow_date: "",
    expected_return_date: "",
  });

  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  function openEditModal(b) {
    setEditError("");
    setEditForm({
      id: b.id,
      event_name: b.event_name || "",
      event_pic_name: b.event_pic_name || "",
      borrow_date: b.borrow_date || "",
      expected_return_date: b.expected_return_date || "",
    });
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!editForm.id) return;

    setEditSaving(true);
    setEditError("");

    try {
      const payload = {
        event_name: (editForm.event_name || "").trim(),
        event_pic_name: (editForm.event_pic_name || "").trim(),
        borrow_date: editForm.borrow_date || null,
        expected_return_date: editForm.expected_return_date || null,
      };

      const res = await fetch(
        `${API_BASE_URL}/api/admin/borrow-batches/${editForm.id}/meta/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data?.detail ||
          (typeof data === "object" ? JSON.stringify(data) : "") ||
          "Failed to save.";
        throw new Error(msg);
      }

      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      setEditOpen(false);
    } catch (e) {
      setEditError(e.message || "Failed to save.");
    } finally {
      setEditSaving(false);
    }
  }

  useEffect(() => {
    async function loadUser() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setUser(data);
      } catch {
        setUser(null);
      } finally {
        setUserLoaded(true);
      }
    }

    if (token) loadUser();
  }, [token]);

  useEffect(() => {
    async function load() {
      if (!userLoaded) return;
      if (!isAdmin) {
        setError("Halaman ini khusus admin/superadmin.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/api/admin/borrow-batches/active/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to load active borrows.");
        const data = await res.json();
        setRows(Array.isArray(data) ? data : data.results || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load active borrows.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token, userLoaded, isAdmin]);

  if (loading) return <p className="text-sm text-muted">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">Active Borrows</h1>
      <p className="text-xs text-muted mb-4">
        Approved batches that are currently active.
      </p>

      {rows.length === 0 ? (
        <p className="text-sm text-muted">No active borrows.</p>
      ) : (
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Ref
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Borrower
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Event
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  PIC
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Items
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Tanggal peminjaman
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Expected return
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Status
                </th>
                {isAdmin && (
                  <th className="px-4 py-2 text-right text-xs font-semibold text-muted">
                    Actions
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {rows.map((b) => {
                const expanded = expandedId === b.id;
                const canReturn = b.status === "APPROVED";

                return (
                  <React.Fragment key={b.id}>
                    <tr className="border-t">
                      <td className="px-4 py-2 text-xs font-mono text-strong">
                        {b.reference_code}
                      </td>

                      <td className="px-4 py-2 text-xs text-strong">
                        {b.user}
                      </td>

                      <td
                        className="px-4 py-2 text-xs text-strong max-w-[220px] truncate"
                        title={typeof getEventName(b) === "string" ? getEventName(b) : ""}
                      >
                        {displayOrDash(getEventName(b))}
                      </td>
                      <td
                        className="px-4 py-2 text-xs text-strong max-w-[220px] truncate"
                        title={typeof getEventPicName(b) === "string" ? getEventPicName(b) : ""}
                      >
                        {displayOrDash(getEventPicName(b))}
                      </td>

                      <td className="px-4 py-2 text-xs">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : b.id)}
                          className="text-brand-700 font-semibold hover:underline"
                        >
                          {expanded ? "Hide" : "Show"} (
                          {(b.items || []).filter((it) => getOutstandingQty(it) > 0).length}
                          )
                        </button>
                      </td>

                      <td className="px-4 py-2 text-xs text-muted">
                        {b.borrow_date || "-"}
                      </td>

                      <td className="px-4 py-2 text-xs text-muted">
                        {b.expected_return_date || "-"}
                      </td>

                      <td className="px-4 py-2 text-xs text-strong">
                        {b.status}
                      </td>

                      {isAdmin && (
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(b)}
                              className="inline-flex items-center px-3 py-1 rounded border border-white/50 dark:border-white/15 bg-brand-500/10 text-brand-700 dark:text-brand-300 text-xs font-semibold"
                              title="Edit event + dates"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              disabled={!canReturn}
                              onClick={() => navigate(`/admin/borrow-batches/${b.id}/return`)}
                              className={
                                "inline-flex items-center px-3 py-1 rounded-md text-[11px] font-semibold " +
                                (canReturn
                                  ? "bg-green-600 text-white hover:bg-green-700"
                                  : "bg-black/5 dark:bg-white/10 text-muted cursor-not-allowed")
                              }
                              title={
                                canReturn
                                  ? "Mark this batch as returned"
                                  : "Only APPROVED batches can be returned"
                              }
                            >
                              Mark as Returned
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>

                    {expanded && (
                      <tr className="border-t bg-brand-500/5">
                        <td colSpan={isAdmin ? 9 : 8} className="px-6 py-3">
                          <div className="text-xs text-strong">
                            {(b.items || [])
                              .filter((it) => getOutstandingQty(it) > 0)
                              .map((it) => (
                                <div
                                  key={it.id ?? `${it.item}-${it.item_name ?? ""}`}
                                  className="flex justify-between py-1 border-b border-white/40 dark:border-white/10 last:border-b-0"
                                >
                                  <span>
                                    {it.item_name || `Item #${it.item}`}
                                  </span>

                                  <span className="font-semibold">
                                    {getOutstandingQty(it)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-lg glass-card">
            <div className="px-5 py-4 border-b border-white/40 dark:border-white/10">
              <div className="text-sm font-semibold text-strong">Edit Active Borrow</div>
              <div className="text-xs text-muted">
                Only edits: event name, PIC name, tanggal peminjaman, expected return.
              </div>
            </div>

            <div className="px-5 py-4 space-y-3">
              {editError && (
                <div className="text-xs text-red-600 break-words">{editError}</div>
              )}

              <div>
                <label className="block text-xs font-semibold text-strong mb-1">
                  Event name
                </label>
                <input
                  className="w-full border border-white/50 dark:border-white/15 rounded-md px-3 py-2 text-sm"
                  value={editForm.event_name}
                  onChange={(e) => setEditForm((p) => ({ ...p, event_name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-strong mb-1">
                  PIC name
                </label>
                <input
                  className="w-full border border-white/50 dark:border-white/15 rounded-md px-3 py-2 text-sm"
                  value={editForm.event_pic_name}
                  onChange={(e) => setEditForm((p) => ({ ...p, event_pic_name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-strong mb-1">
                    Tanggal peminjaman
                  </label>
                  <input
                    type="date"
                    className="w-full border border-white/50 dark:border-white/15 rounded-md px-3 py-2 text-sm"
                    value={editForm.borrow_date || ""}
                    onChange={(e) => setEditForm((p) => ({ ...p, borrow_date: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-strong mb-1">
                    Expected return
                  </label>
                  <input
                    type="date"
                    className="w-full border border-white/50 dark:border-white/15 rounded-md px-3 py-2 text-sm"
                    value={editForm.expected_return_date || ""}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, expected_return_date: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-white/40 dark:border-white/10 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                disabled={editSaving}
                className="px-3 py-2 text-xs font-semibold rounded-md bg-black/5 dark:bg-white/10 text-strong hover:bg-slate-300 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editSaving}
                className="px-3 py-2 text-xs font-semibold rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
              >
                {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}